import { computedFn } from 'mobx-utils/lib/computedFn';
import { createAppButton } from 'owa-command-ribbon';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { getComposeRibbonId } from 'owa-editor/lib/utils/getComposeRibbonId';
import type EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import type { IButtonStyles } from '@fluentui/react';
import { FormatControlId } from 'owa-ribbon-ids';
import loc from 'owa-localize';
import { callFormatApi } from 'owa-editor/lib/utils/callFormatApi';
import { callEditorApi } from 'owa-editor/lib/utils/callEditorApi';
import {
    tableControlMenuSplitHorizontally,
    tableControlMenuSplitVertically,
    tableControlMenuDeleteRow,
    tableControlMenuDeleteColumn,
    tableControlMenuDeleteTable,
    tableControlMenuMergeAbove,
    tableControlMenuMergeBelow,
    tableControlMenuMergeLeft,
    tableControlMenuMergeRight,
    tableControlMenuInsertAbove,
    tableControlMenuInsertBelow,
    tableControlMenuInsertLeft,
    tableControlMenuInsertRight,
    tableControlMenuAlignCellBottom,
    tableControlMenuAlignCellCenter,
    tableControlMenuAlignCellLeft,
    tableControlMenuAlignCellMiddle,
    tableControlMenuAlignCellRight,
    tableControlMenuAlignCellTop,
} from './tableControlsStrings.locstring.json';
import { TableOperation } from 'roosterjs-editor-types';

export const getTableOperationControl = computedFn(
    (
        viewState: EditorViewState | undefined,
        controlId: FormatControlId,
        styles: IButtonStyles | undefined
    ): RibbonControlProps => {
        let label = '';
        let tableOperation: TableOperation;

        switch (controlId) {
            case FormatControlId.InsertRowAbove:
                label = tableControlMenuInsertAbove;
                tableOperation = TableOperation.InsertAbove;
                break;
            case FormatControlId.InsertRowBelow:
                label = tableControlMenuInsertBelow;
                tableOperation = TableOperation.InsertBelow;
                break;
            case FormatControlId.InsertColumnLeft:
                label = tableControlMenuInsertLeft;
                tableOperation = TableOperation.InsertLeft;
                break;
            case FormatControlId.InsertColumnRight:
                label = tableControlMenuInsertRight;
                tableOperation = TableOperation.InsertRight;
                break;
            case FormatControlId.DeleteTable:
                label = tableControlMenuDeleteTable;
                tableOperation = TableOperation.DeleteTable;
                break;
            case FormatControlId.DeleteColumns:
                label = tableControlMenuDeleteColumn;
                tableOperation = TableOperation.DeleteColumn;
                break;
            case FormatControlId.DeleteRows:
                label = tableControlMenuDeleteRow;
                tableOperation = TableOperation.DeleteRow;
                break;
            case FormatControlId.MergeAbove:
                label = tableControlMenuMergeAbove;
                tableOperation = TableOperation.MergeAbove;
                break;
            case FormatControlId.MergeBelow:
                label = tableControlMenuMergeBelow;
                tableOperation = TableOperation.MergeBelow;
                break;
            case FormatControlId.MergeLeft:
                label = tableControlMenuMergeLeft;
                tableOperation = TableOperation.MergeLeft;
                break;
            case FormatControlId.MergeRight:
                label = tableControlMenuMergeRight;
                tableOperation = TableOperation.MergeRight;
                break;
            case FormatControlId.SplitCellsHorizontally:
                label = tableControlMenuSplitHorizontally;
                tableOperation = TableOperation.SplitHorizontally;
                break;
            case FormatControlId.SplitCellsVertically:
                label = tableControlMenuSplitVertically;
                tableOperation = TableOperation.SplitVertically;
                break;
            case FormatControlId.AlignTableCellCenter:
                label = tableControlMenuAlignCellCenter;
                tableOperation = TableOperation.AlignCellCenter;
                break;
            case FormatControlId.AlignTableCellLeft:
                label = tableControlMenuAlignCellLeft;
                tableOperation = TableOperation.AlignCellLeft;
                break;
            case FormatControlId.AlignTableCellRight:
                label = tableControlMenuAlignCellRight;
                tableOperation = TableOperation.AlignCellRight;
                break;
            case FormatControlId.AlignTableCellTop:
                label = tableControlMenuAlignCellTop;
                tableOperation = TableOperation.AlignCellTop;
                break;
            case FormatControlId.AlignTableCellMiddle:
                label = tableControlMenuAlignCellMiddle;
                tableOperation = TableOperation.AlignCellMiddle;
                break;
            case FormatControlId.AlignTableCellBottom:
                label = tableControlMenuAlignCellBottom;
                tableOperation = TableOperation.AlignCellBottom;
                break;
        }

        const flyoutAnchor = createAppButton(
            getComposeRibbonId(controlId, viewState),
            loc(label),
            () => executeTableOperation(viewState, tableOperation),
            undefined /**keytipProps */,
            undefined /** icon */,
            undefined /**iconColor */,
            styles
        );

        return flyoutAnchor;
    }
);

const executeTableOperation = (viewState: EditorViewState | undefined, action: TableOperation) => {
    if (!!viewState) {
        callEditorApi(viewState, 'focus');
        callFormatApi(viewState, 'editTable', [action]);
    }
};
